import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <HeroSection />
      
      {/* Digital Transformation Stats */}
      <DigitalTransformationSection />
      
      {/* Mission Section */}
      <MissionSection />
      
      {/* Services Section */}
      <ServicesSection />
      
      {/* Tools & Technologies */}
      <ToolsSection />

      {/* Industries We Serve Section */}
      <IndustriesSection />
      
      {/* Testimonials */}
      <TestimonialsSection />
      
      {/* Recognition Section */}
      <RecognitionSection />

       {/* Meet Our Experts */}
      <MeetExpertsSection />
      
      {/* Newsletter Subscription - NEW
      <NewsletterSection /> */}
    </div>
  );
};

// Hero Section - UPDATED
const HeroSection = () => {
  return (
    <section 
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a8c 50%, #3d5a9c 100%)'
      }}
    >
      {/* AI Brain Circuit Image on Right */}
      <div 
        className="absolute right-0 top-0 bottom-0 w-1/2 opacity-80"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          maskImage: 'linear-gradient(to left, rgba(0,0,0,0.8), transparent)',
          WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.8), transparent)'
        }}
      />

      {/* Hexagonal Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l26 15v30L30 60 4 45V15z' fill='none' stroke='%23fff' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px',
          backgroundRepeat: 'repeat'
        }}
      />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="max-w-2xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
            Empowering Your Business with Technologically <span className="text-green-400">Innovative Digital Solutions</span>
          </h1>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
            >
              Schedule A Call 
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-3 rounded-full hover:bg-white hover:text-blue-900 transition-colors font-medium"
            >
              Explore More →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
// Digital Transformation Stats Section
const DigitalTransformationSection = () => {
  const countersRef = useRef([]);

  useEffect(() => {
    const options = {
      root: null,
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          const target = +counter.getAttribute('data-target');
          const increment = target / 100;
          counter.style.opacity = 1;
          let count = 0;

          const updateCounter = () => {
            count += increment;
            if (count < target) {
              counter.textContent = Math.ceil(count) + '+';
              requestAnimationFrame(updateCounter);
            } else {
              counter.textContent = target + '+';
            }
          };

          updateCounter();
          observer.unobserve(counter);
        }
      });
    }, options);

    countersRef.current.forEach((counter) => {
      if (counter) observer.observe(counter);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-gray-600 mb-2">We're here to deliver 100% tech-driven solutions.</p>
          <h2 className="text-4xl font-bold text-gray-900">
            Empowering Your <span className="text-green-600">Digital Transformation</span> Journey
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {/* Digital Projects */}
          <div>
            <p 
              ref={(el) => (countersRef.current[0] = el)}
              className="text-5xl font-bold text-green-600 opacity-0 transition-opacity duration-500"
              data-target="45"
              style={{ opacity: 0 }}
            >
              0+
            </p>
            <p className="text-gray-900 font-medium mt-2">Digital Projects Successfully Delivered</p>
          </div>

          {/* Industries Catered */}
          <div>
            <p 
              ref={(el) => (countersRef.current[1] = el)}
              className="text-5xl font-bold text-green-600 opacity-0 transition-opacity duration-500"
              data-target="25"
              style={{ opacity: 0 }}
            >
              0+
            </p>
            <p className="text-gray-900 font-medium mt-2">Industries Catered</p>
          </div>

          {/* Client Satisfaction */}
          <div>
            <p 
              ref={(el) => (countersRef.current[2] = el)}
              className="text-5xl font-bold text-green-600 opacity-0 transition-opacity duration-500"
              data-target="94"
              style={{ opacity: 0 }}
            >
              0+
            </p>
            <p className="text-gray-900 font-medium mt-2">Client Satisfaction Rate</p>
          </div>

          {/* Years of Expertise */}
          <div>
            <p 
              ref={(el) => (countersRef.current[3] = el)}
              className="text-5xl font-bold text-green-600 opacity-0 transition-opacity duration-500"
              data-target="2"
              style={{ opacity: 0 }}
            >
              0+
            </p>
            <p className="text-gray-900 font-medium mt-2">Years of Expertise</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              Empowering Businesses with Technology Solutions
            </h2>
            <p className="text-gray-600 mb-6">
              At Plexgate, our mission is to provide innovative tech solutions that empower businesses. 
              Our experienced team is dedicated to exceptional customer service and reliable support for 
              all your technology needs.
            </p>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Our Commitment to Excellence
            </h3>
            <p className="text-gray-600">
              With expertise in IT support, cloud solutions, cybersecurity, and more, Plexgate is 
              committed to helping businesses thrive through cutting-edge technology and exceptional 
              service tailored to your unique needs.
            </p>
          </div>
          <div className="w-full h-[280px]">
  <img
    src="/images/mission-image.jpg"
    alt="Technology workspace"
    className="w-full h-full object-cover rounded-lg shadow-lg"
    onError={(e) => {
      e.target.parentElement.innerHTML =
        '<div class="bg-gray-300 rounded-lg h-full flex items-center justify-center"><span class="text-gray-500">Mission Image Placeholder</span></div>';
    }}
  />
</div>

        </div>
      </div>
    </section>
  );
};

// Services Section with Auto-Scrolling Carousel (8 Services)
const ServicesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Background images for each service card
  const backgroundImages = [
    'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600', // Application Development - code
    'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600', // Cybersecurity - lock/security
    'https://images.unsplash.com/photo-1547658719-da2b51169166?w=600', // Web Development - laptop
    'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600', // IT Support - keyboard
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600', // Cloud - digital network
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600', // IT Consultation - analytics
    'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600', // Creative Design - design tools
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600', // Training - learning
    'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600', // Recruitment - team
  ];

  const services = [
    {
      icon: 'https://img.icons8.com/color/64/code.png',
      title: 'Application Development',
      description: 'From mobile apps to enterprise-level software, we specialize in crafting custom applications that enhance productivity and user engagement.',
      features: [
        'Mobile App Development',
        'Web Applications',
        'Enterprise Software',
        'Custom Solutions'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/security-configuration.png',
      title: 'Cybersecurity Services',
      description: 'Protect your business from online threats with our comprehensive cybersecurity solutions.',
      features: [
        'Security Audits',
        'Threat Detection',
        'Data Protection',
        'Compliance Management'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/web.png',
      title: 'Web Development',
      description: 'We create dynamic, user-friendly, and responsive websites tailored to your business needs.',
      features: [
        'Responsive Design',
        'E-commerce Solutions',
        'CMS Development',
        'Website Maintenance'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/technical-support.png',
      title: 'IT/Desktop Support',
      description: 'Reliable IT support services ensuring minimal downtime and maximum productivity.',
      features: [
        '24/7 Support',
        'Help Desk Services',
        'System Maintenance',
        'Troubleshooting'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/cloud.png',
      title: 'Cloud Solutions',
      description: 'Scalable and secure cloud-based solutions for your business infrastructure.',
      features: [
        'Cloud Migration',
        'Cloud Storage',
        'Cloud Security',
        'Infrastructure Management'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/data-backup.png',
      title: 'IT Consultation & Auditing',
      description: 'Empower your organization with expert guidance and transparent insights.',
      features: [
        'IT Strategy',
        'System Audits',
        'Technology Consulting',
        'Performance Optimization'
      ]
    },
    // {
    //   icon: 'https://img.icons8.com/color/64/design.png',
    //   title: 'Creative Design',
    //   description: 'Create compelling web designs using UI/UX & design services to improve responsiveness & user experience.',
    //   features: [
    //     'Graphic Designing',
    //     'UI/UX Designing',
    //     'Branding',
    //     'Mobile App Designing',
    //     'Web Designing',
    //     'Logo Designing'
    //   ]
    // },
    {
      icon: 'https://img.icons8.com/color/64/training.png',
      title: 'Tech Training & Certification Guidance',
      description: 'Empowering individuals to break into tech through hands-on training, structured learning paths, and expert guidance on industry-recognized certifications.',
      features: [
        'Beginner to Advanced Tech Training',
        'Career Path Guidance into Tech Roles',
        'Certification Roadmaps (AWS, Azure, CompTIA, Google, etc.)',
        'Exam Preparation & Study Support'
      ]
    },
    {
      icon: 'https://img.icons8.com/color/64/technical-support.png',
      title: 'Recruitment & Talent Solutions',
      description: 'Connecting top tech talent with the right opportunities through strategic recruitment and talent matching.',
      features: [
        'Tech Talent Sourcing',
        'Candidate Screening & Vetting',
        'Contract & Permanent Placements',
        'Employer–Candidate Matching'
      ]
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % services.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [services.length]);

  // Get 3 visible cards
  const getVisibleCards = () => {
    const cards = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % services.length;
      cards.push({ ...services[index], key: index });
    }
    return cards;
  };

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-4xl font-bold">
            <span className="text-green-600">Services</span> We Offer
          </h2>
          <Link
            to="/services"
            className="px-6 py-2 border-2 border-gray-900 rounded-full hover:bg-gray-900 hover:text-white transition-colors font-medium"
          >
            View All Services →
          </Link>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="grid md:grid-cols-3 gap-6">
            {getVisibleCards().map((service) => (
              <Link
                key={service.key}
                to="/services"
                className="relative rounded-2xl p-8 min-h-[350px] flex flex-col transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer overflow-hidden group"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{
                    backgroundImage: `url('${backgroundImages[service.key]}')`,
                    filter: 'brightness(0.25)'
                  }}
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-blue-900/85 to-gray-900/90" />
                
                {/* Content */}
                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon */}
                  <div className="mb-4">
                    <img 
                      src={service.icon} 
                      alt={service.title} 
                      className="w-16 h-16"
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>

                  {/* Description */}
                  <p className="text-gray-300 text-sm mb-4">{service.description}</p>

                  {/* Features (show first 4) */}
                  <div className="space-y-2 mt-auto">
                    {service.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-green-400 text-lg">→</span>
                        <span className="text-sm text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSlide ? 'bg-gray-900 w-8' : 'bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Full-stack Expertise Section (with tabs)
const ToolsSection = () => {
  const [activeTab, setActiveTab] = React.useState('frameworks');

  const technologies = {
    frameworks: [
      { name: 'React', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg' },
      { name: 'AngularJS', logo: 'https://tse3.mm.bing.net/th/id/OIP.liYbsPpCk8BCLhNs9R9Z4wHaE8?rs=1&pid=ImgDetMain&o=7&rm=3' },
      { name: 'Vue.js', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg' },
      { name: 'Next.js', logo: 'https://upload.wikimedia.org/wikipedia/commons/8/8e/Nextjs-logo.svg' },
      { name: 'jQuery', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/fd/JQuery-Logo.svg' },
      { name: '.NET', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7d/Microsoft_.NET_logo.svg' },
      { name: 'Node.js', logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg' },
      { name: 'Nest.js', logo: 'https://nestjs.com/img/logo-small.svg' },
      { name: 'Spring', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Spring_Framework_Logo_2018.svg' },
      { name: 'Laravel', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg' },
      { name: 'Rails', logo: 'https://upload.wikimedia.org/wikipedia/commons/6/62/Ruby_On_Rails_Logo.svg' },
      { name: 'Flutter', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/17/Google-flutter-logo.png' },
      { name: 'Xamarin', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f2/Xamarin-logo.svg' },
      { name: 'React Native', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg' },
      { name: '.NET MAUI', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7d/Microsoft_.NET_logo.svg' }
    ],
    languages: [
      { name: 'JavaScript', logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6a/JavaScript-logo.png' },
      { name: 'TypeScript', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/4c/Typescript_logo_2020.svg' },
      { name: 'Python', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg' },
      { name: 'Java', logo: 'https://upload.wikimedia.org/wikipedia/en/3/30/Java_programming_language_logo.svg' },
      { name: 'C#', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/4f/Csharp_Logo.png' },
      { name: 'PHP', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/27/PHP-logo.svg' },
      { name: 'Ruby', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/73/Ruby_logo.svg' },
      { name: 'Go', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/05/Go_Logo_Blue.svg' }
    ],
    platforms: [
      { name: 'WordPress', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/20/WordPress_logo.svg' },
      { name: 'Shopify', logo: 'https://cdn.shopify.com/assets/images/logos/shopify-bag.png' },
      { name: 'Wix', logo: 'https://www.liblogo.com/img-logo/wi8805l3b0-wix-logo-logo-wix-icon-free-download-on-iconfinder.png' },
      { name: 'Magento', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/55/Magento_Logo.svg' },
      { name: 'Drupal', logo: 'https://www.pngall.com/wp-content/uploads/12/Drupal-PNG-Image-File.png' },
      { name: 'Joomla', logo: 'https://icons.iconarchive.com/icons/sicons/basic-round-social/512/joomla-icon.png' }
    ],
    database: [
      { name: 'MySQL', logo: 'https://upload.wikimedia.org/wikipedia/en/d/dd/MySQL_logo.svg' },
      { name: 'PostgreSQL', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/29/Postgresql_elephant.svg' },
      { name: 'MongoDB', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/93/MongoDB_Logo.svg' },
      { name: 'Redis', logo: 'https://cdn4.iconfinder.com/data/icons/redis-2/1451/Untitled-2-512.png' },
      { name: 'SQLite', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/97/Sqlite-square-icon.svg' },
      { name: 'Oracle', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg' },
      { name: 'Microsoft SQL Server', logo: 'https://upload.wikimedia.org/wikipedia/de/8/8c/Microsoft_SQL_Server_Logo.svg' },
      { name: 'Firebase', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/37/Firebase_Logo.svg' },
      { name: 'Supabase', logo: 'https://logowik.com/content/uploads/images/supabase-icon9119.logowik.com.webp' }
    ]
  };

  const tabs = [
    { id: 'frameworks', label: 'Frame Works' },
    { id: 'languages', label: 'Programming Languages' },
    { id: 'platforms', label: 'Platforms' },
    { id: 'database', label: 'Database' }
  ];

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            We Offer <span className="text-green-600">Full-stack Expertise</span> At Every Step Of The Process
          </h2>
          <p className="text-gray-600 max-w-4xl mx-auto">
            Plexgate ensures excellence in every project & builds dynamic and secure web solutions using 
            the groundbreaking & latest technology.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-green-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Technology Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 gap-4">
          {technologies[activeTab].map((tech, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center justify-center hover:shadow-xl transition-shadow duration-300"
            >
              <img 
                src={tech.logo} 
                alt={tech.name} 
                className="h-16 w-16 object-contain mb-3"
              />
              <span className="text-sm text-gray-700 font-medium text-center">{tech.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Industries We Serve Section - NEW
const IndustriesSection = () => {
  const industries = [
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
          <circle cx="12" cy="12" r="3"/>
          <line x1="12" y1="2" x2="12" y2="9"/>
          <line x1="12" y1="15" x2="12" y2="22"/>
        </svg>
      ),
      title: 'Healthcare',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M3 9h18"/>
          <path d="M9 21V9"/>
        </svg>
      ),
      title: 'Finance',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
          <path d="M12 6v6l4 2"/>
        </svg>
      ),
      title: 'Service Providers',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      ),
      title: 'Retail',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="4" width="16" height="16" rx="2"/>
          <rect x="9" y="9" width="6" height="6"/>
          <path d="M9 1v6"/>
          <path d="M15 1v6"/>
          <path d="M9 15v6"/>
          <path d="M15 15v6"/>
          <path d="M20 9h-6"/>
          <path d="M20 14h-6"/>
          <path d="M4 9h6"/>
          <path d="M4 14h6"/>
        </svg>
      ),
      title: 'Nonprofit',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="7" width="20" height="14" rx="2"/>
          <path d="M16 3v4"/>
          <path d="M8 3v4"/>
          <path d="M10 12h4"/>
          <path d="M12 10v4"/>
        </svg>
      ),
      title: 'Manufacturing',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      ),
      title: 'Utilities',
    },
    {
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      ),
      title: 'Postal Mailers',
    },
  ];

  return (
    <section
  className="py-16"
  style={{ backgroundColor: '#f3f4f6' }}
>

      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Industries we serve
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Plexgate provides an array of solutions, products and services specifically designed for these industries and more.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-12 md:gap-16">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center group"
            >
              <div className="text-gray-700 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                {industry.icon}
              </div>
              <h3 className="text-gray-900 font-medium text-sm">{industry.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shuffledTestimonials, setShuffledTestimonials] = useState([]);

    const allTestimonials = [
    {
      name: 'Hassan Al-Farsi',
      quote: "I'm glad I chose Plexgate for software development services. The team was competent and highly skilled & delivered error-free solutions.",
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Sarah Mitchell',
      quote: 'Looking for a trustworthy digital solutions provider? I have worked with Plexgate & I must say they have highly skilled team members who understand your requirements and come up with innovative ideas.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Emily Carter',
      quote: 'Being a long-term client of Plexgate, I would like to share my experience of QA & Support services. The team was fantastic as they were efficient and fixed all the bugs. They meet fast-changing customer demands. Highly recommended!!!',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Michael Rodriguez',
      quote: 'Plexgate transformed our entire IT infrastructure. Their cloud solutions are top-notch and the migration process was seamless. Excellent communication throughout the project.',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Jennifer Lee',
      quote: 'Outstanding cybersecurity services! Plexgate helped us identify and fix vulnerabilities we didn\'t even know existed. Our business is now more secure than ever.',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'David Thompson',
      quote: 'The web development team at Plexgate is exceptional. They created a beautiful, responsive website that perfectly represents our brand. Very professional and timely delivery.',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Lisa Anderson',
      quote: 'We needed reliable IT support and Plexgate delivered beyond our expectations. Their response time is incredible and they always solve issues quickly. Highly recommend their services!',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Robert Chen',
      quote: 'Plexgate\'s application development expertise is outstanding. They built a custom solution that streamlined our operations and increased productivity by 40%. Worth every penny!',
      image: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=150&h=150&fit=crop&crop=faces'
    },
    {
      name: 'Amanda Williams',
      quote: 'Professional, knowledgeable, and reliable. Plexgate has been our IT partner for over 2 years and they consistently exceed expectations. Their consultation services helped us make smart technology decisions.',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=faces'
    }
  ];

  // Shuffle function
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Initialize shuffled testimonials on component mount
  useEffect(() => {
    setShuffledTestimonials(shuffleArray(allTestimonials));
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => {
      const next = prev + 3;
      // If we've shown all testimonials, reshuffle and start over
      if (next >= shuffledTestimonials.length) {
        setShuffledTestimonials(shuffleArray(allTestimonials));
        return 0;
      }
      return next;
    });
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => {
      const previous = prev - 3;
      if (previous < 0) {
        return shuffledTestimonials.length - 3;
      }
      return previous;
    });
  };

  useEffect(() => {
    if (shuffledTestimonials.length > 0) {
      const interval = setInterval(nextSlide, 1500);
      return () => clearInterval(interval);
    }
  }, [shuffledTestimonials, currentSlide]);

  if (shuffledTestimonials.length === 0) return null;

  const totalSlides = Math.ceil(shuffledTestimonials.length / 3);

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What Our <span className="text-green-600">Customers</span> Say About Us
          </h2>
          <p className="text-gray-600 max-w-4xl mx-auto">
            Our team ensures that our clients get what they expect from Plexgate. With our dedication & 
            years of experience in this industry, we have successfully built long-term relationships with 
            our clients. Read the following feedback to understand how we cooperate with our clients.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          <div className="grid md:grid-cols-3 gap-8">
            {[0, 1, 2].map((offset) => {
              const index = currentSlide + offset;
              if (index >= shuffledTestimonials.length) return null;
              
              const testimonial = shuffledTestimonials[index];
              
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg p-8 transition-all duration-500"
                >
                  {/* 5 Stars */}
                  <div className="flex mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className="w-6 h-6 text-yellow-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-gray-600 mb-6 min-h-[120px]">{testimonial.quote}</p>

                  {/* Author */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 overflow-hidden">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xl">👤</div>';
                        }}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index * 3)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  Math.floor(currentSlide / 3) === index ? 'bg-gray-800 w-8' : 'bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Recognition Section (Your pre-coded component)
const RecognitionSection = () => {
  const partners = [
  { name: 'HP Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ad/HP_logo_2012.svg' },
  { name: 'Microsoft Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg' },
  { name: 'Cisco Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Cisco_logo_blue_2016.svg' },
  { name: 'Lenovo Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b8/Lenovo_logo_2015.svg' },
  { name: 'Dell Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/8/82/Dell_Logo.png' },
  { name: 'IBM Partner', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg' },
];

  return (
    <section className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="uppercase text-sm font-bold text-gray-500 mb-2">Who We Are</p>
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4">Recognized by the Best</h2>
            <p className="text-gray-600 leading-relaxed">
              At Plexgate, we pride ourselves on delivering cutting-edge technology solutions trusted 
              by the best in the industry. Our commitment to excellence ensures seamless project delivery, 
              saving our clients time and resources while driving success.
            </p>
          </div>

          <div className="overflow-hidden">
            <div className="flex gap-4 animate-scroll">
              {partners.concat(partners).map((partner, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 flex justify-center items-center"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="h-20 w-32 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll {
          from {
            transform: translateX(0%);
          }
          to {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 20s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

// Meet Our Experts Section
const MeetExpertsSection = () => {
  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-screen-2xl mx-auto px-4">
        <div 
          className="relative bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 rounded-2xl px-8 md:px-16 py-12 overflow-hidden"
          style={{
            background: '#2d4a8c'
          }}
        >
          {/* Circuit board pattern overlay */}
          <div 
            className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h80v80H10z' fill='none' stroke='%23fff' stroke-width='1'/%3E%3Ccircle cx='10' cy='10' r='3' fill='%23fff'/%3E%3Ccircle cx='90' cy='10' r='3' fill='%23fff'/%3E%3Ccircle cx='10' cy='90' r='3' fill='%23fff'/%3E%3Ccircle cx='90' cy='90' r='3' fill='%23fff'/%3E%3C/svg%3E")`,
              backgroundSize: '100px 100px',
              backgroundRepeat: 'repeat'
            }}
          />

          {/* Content */}
          <div className="relative z-10 max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Meet <span className="text-green-500">our experts</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Book a consultation with us and select a time slot that suits you best.
            </p>
            <Link
              to="/contact"  // ✅ This points to the Contact page
              className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
            >
              Book a Consultation →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};


// Newsletter Subscription Section - NEW
const NewsletterSection = () => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add your newsletter subscription logic here
    console.log('Newsletter subscription:', email);
    alert('Thank you for subscribing to our newsletter!');
    setEmail('');
  };

  return (
    <section 
      className="py-16"
      style={{
        background: '#2d4a8c'
      }}
    >
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-4xl font-bold text-white mb-6">
          Subscribe to <span className="text-green-500">Our Newsletter</span>
        </h2>
        <p className="text-white text-lg mb-4">
          Receive the latest technology & business news in your inbox.
        </p>
        <p className="text-gray-300 mb-8">
          Select the newsletters you'd like to receive.
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            type="submit"
            className="bg-gray-700 text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            Subscribe
          </button>
        </form>
      </div>
    </section>
  );
};

export default Home;